import { useQueries, type UseQueryResult } from "@tanstack/react-query";

import { backofficeClient } from "@/lib/api/backoffice-client";
import {
  mergeHistories,
  type BroadcastHistoryItem,
} from "@/lib/broadcast/fan-out";
import {
  CELLS,
  PRIMARY_CELL,
  isRemoteCell,
  type Cell,
} from "@/lib/cells/cells";
import { cellQueryPolicy } from "@/lib/cells/cell-session";
import type { CellFailure } from "@/lib/cells/cell-tenants";

// The primary cell keeps the query key these queries had before cells, so a
// single-cell deployment fetches and caches exactly what it did.
const keyFor = (base: unknown[], cell: Cell) =>
  isRemoteCell(cell) ? [...base, cell.id] : base;

const policyFor = (cell: Cell) =>
  isRemoteCell(cell) ? cellQueryPolicy(cell) : {};

function failuresOf(results: UseQueryResult[]): CellFailure[] {
  return CELLS.flatMap((cell, index) => {
    // A failed background refetch keeps the data it already has.
    const result = results.at(index);
    const error = result?.data ? null : result?.error;
    return error ? [{ cell, error }] : [];
  });
}

export interface BroadcastAudience {
  total: number;
  tenants: {
    tenant_id: string;
    company: string | null;
    customer_stage: string;
  }[];
}

/**
 * Who a stage audience reaches in every cell, each cell resolving its own
 * customers. `error` is set when any cell couldn't resolve its share, since a
 * send would then miss that cell's tenants.
 */
export function useCellAudience(stages: string[], enabled: boolean) {
  return useQueries({
    queries: CELLS.map((cell) => ({
      queryKey: keyFor(["broadcast-audience", stages], cell),
      enabled,
      ...policyFor(cell),
      queryFn: async () => {
        const response = await backofficeClient.get<BroadcastAudience>(
          "/broadcasts/audience",
          {
            params: { stages },
            paramsSerializer: { indexes: null },
            routeCellId: isRemoteCell(cell) ? cell.id : undefined,
          },
        );
        return response.data;
      },
    })),
    combine: (results) => {
      const failures = failuresOf(results);
      const answered = results.flatMap((result) =>
        result.data ? [result.data] : [],
      );
      return {
        data:
          answered.length === CELLS.length
            ? {
                total: answered.reduce(
                  (sum, audience) => sum + audience.total,
                  0,
                ),
                tenants: answered.flatMap((audience) => audience.tenants),
              }
            : undefined,
        isFetching: results.some((result) => result.isFetching),
        error: failures.length > 0 ? failures[0].error : null,
        failures,
      };
    },
  });
}

/**
 * Broadcast history from every cell, merged by idempotent id so a broadcast
 * that reached several cells is one row. A cell that can't be read is in
 * `failures`; `error` is set only when no cell could be read.
 */
export function useCellHistory() {
  return useQueries({
    queries: CELLS.map((cell) => ({
      queryKey: keyFor(["broadcast-history"], cell),
      ...policyFor(cell),
      queryFn: async () => {
        const response = await backofficeClient.get<{
          total: number;
          items: BroadcastHistoryItem[];
        }>("/broadcasts", {
          routeCellId: isRemoteCell(cell) ? cell.id : undefined,
        });
        return response.data;
      },
    })),
    combine: (results) => {
      const primary = results.at(CELLS.indexOf(PRIMARY_CELL));
      if (CELLS.length === 1) {
        return {
          data: primary?.data,
          isLoading: !!primary?.isLoading,
          error: primary?.error ?? null,
          failures: [],
        };
      }
      const failures = failuresOf(results);
      const answered = results.flatMap((result) =>
        result.data ? [result.data] : [],
      );
      return {
        data: answered.length > 0 ? mergeHistories(answered) : undefined,
        isLoading:
          results.some((result) => result.isLoading) && answered.length === 0,
        error:
          failures.length === CELLS.length
            ? (failures[0]?.error ?? null)
            : null,
        failures,
      };
    },
  });
}
